import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Skeleton,
  Switch,
  CircularProgress,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import moment from "moment";
import FilterPreview from "./FilterViewer/FilterViewer";
import { getCubeFiltersByActionType } from "../../../helper/Quadrant/quadrantHelper";
import Styles from "../StopLossList.module.css";

const stickyLeftStyle = (left = 0, bgColor = "#ffffff") => ({
  position: "sticky",
  left,
  zIndex: 3,
  background: bgColor,
  borderRight: "1px solid #ddd",
  color: "#00000099",
  fontWeight: 600,
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
});

const stickyRightStyle = (right = 0, bgColor = "#ffffff") => ({
  position: "sticky",
  right,
  zIndex: 3,
  background: bgColor,
  borderLeft: "1px solid #ddd",
  fontWeight: 600,
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
  textAlign: "center",
  color: "#00000099",
});

const commonCellStyle = (bgColor = "#ffffff") => ({
  background: bgColor,
  fontWeight: 600,
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
  padding: "6px 16px",
  borderRight: "1px solid #ddd",
  minWidth: 120,
});

const bodyCellStyle = {
  fontSize: "0.75rem",
  padding: "16px",
  background: "white",
  borderRight: "1px solid #ddd",
  minWidth: 120,
  verticalAlign: "middle",
};

const zIndexes = {
  header: 5,
  stickyColumn: 6,
};

const MIDDLE_COLUMNS = [
  { fieldName: "Updated At", id: "updated_at", minWidth: 140 },
  { fieldName: "Frequency", id: "frequency", minWidth: 150 },
  { fieldName: "Exclusion Case", id: "exclusion_case", minWidth: 200 },
  { fieldName: "Inclusion Case", id: "inclusion_case", minWidth: 200 },
];

function StopLossTable({
  ruleList = [],
  isLoading = false,
  updatingRuleIds = [],
  onStatusChange,
  onEdit,
  onLogs,
}) {
  if (!isLoading && ruleList.length === 0) {
    return (
      <Paper
        sx={{
          width: "100%",
          border: "1px solid #ddd",
          borderRadius: "8px",
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 250,
            color: "text.secondary",
            gap: 1.5,
          }}
        >
          <SearchOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
          <Typography variant="subtitle1">No Data Found</Typography>
        </Box>
      </Paper>
    );
  }

  const renderCellValue = (rowData, columnId) => {
    switch (columnId) {
      case "updated_at":
        return rowData.updated_at
          ? moment(rowData.updated_at).format("MMM DD, YYYY")
          : "-";
      case "frequency":
        return rowData?.schedule_config?.day_frequency ? (
          <span className={Styles.blueBadge}>
            Running {rowData.schedule_config.day_frequency}
          </span>
        ) : (
          "-"
        );
      case "exclusion_case":
        return FilterPreview({
          filters: JSON.parse(
            getCubeFiltersByActionType(rowData?.rule_tasks, "PAUSE_PRODUCTS") ??
              "{}"
          )?.query?.filters,
        });
      case "inclusion_case":
        return FilterPreview({
          filters: JSON.parse(
            getCubeFiltersByActionType(
              rowData?.rule_tasks,
              "ENABLE_PRODUCTS"
            ) ?? "{}"
          )?.query?.filters,
        });
      default:
        return "-";
    }
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "auto",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "none",
      }}
    >
      <TableContainer sx={{ maxHeight: 600, minHeight: 400 }}>
        <Table stickyHeader size="small" sx={{ borderCollapse: "separate" }}>
          <TableHead>
            <TableRow
              sx={{ zIndex: zIndexes.header, position: "sticky", top: 0 }}
            >
              <TableCell
                sx={{
                  ...stickyLeftStyle(0),
                  padding: "16px",
                  minWidth: 80,
                  zIndex: zIndexes.stickyColumn,
                  textAlign: "center",
                  borderBottom: "1px solid #ddd",
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  ...stickyLeftStyle(80),
                  padding: "6px 16px",
                  minWidth: 200,
                  zIndex: zIndexes.stickyColumn,
                  borderBottom: "1px solid #ddd",
                }}
              >
                Rule Name
              </TableCell>

              {MIDDLE_COLUMNS.map((th) => (
                <TableCell
                  key={th.id}
                  sx={{
                    ...commonCellStyle(),
                    borderBottom: "1px solid #ddd",
                    zIndex: zIndexes.header,
                    minWidth: th.minWidth,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#00000099",
                    }}
                  >
                    {th.fieldName}
                  </Typography>
                </TableCell>
              ))}

              <TableCell
                sx={{
                  ...stickyRightStyle(0),
                  zIndex: zIndexes.stickyColumn,
                  borderBottom: "1px solid #ddd",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? [...Array(10)].map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        background: "white",
                        zIndex: 2,
                        minWidth: 80,
                        borderRight: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Skeleton variant="rounded" width={40} height={12} />
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 80,
                        background: "white",
                        zIndex: 2,
                        minWidth: 200,
                        borderRight: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Skeleton variant="text" width="60%" height={20} />
                    </TableCell>
                    {MIDDLE_COLUMNS.map((col) => (
                      <TableCell
                        key={`skeleton-${idx}-${col.id}`}
                        sx={bodyCellStyle}
                      >
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 2,
                        borderLeft: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Box display="flex" gap={1}>
                        {[...Array(2)].map((_, iconIdx) => (
                          <Skeleton
                            key={iconIdx}
                            variant="circular"
                            width={24}
                            height={24}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : ruleList.map((rowData, index) => (
                  <TableRow
                    key={rowData.id || index}
                    hover
                    sx={{
                      "&:hover td": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        background: "white",
                        zIndex: 2,
                        minWidth: 80,
                        borderRight: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: 24,
                          minWidth: 40,
                        }}
                      >
                        {updatingRuleIds.includes(rowData?.id) ? (
                          <CircularProgress size={20} sx={{ color: "#6759FF" }} />
                        ) : (
                          <Switch
                            checked={rowData?.status === "Active"}
                            onChange={(e) =>
                              onStatusChange(rowData?.id, e.target.checked)
                            }
                            size="small"
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 80,
                        background: "white",
                        zIndex: 2,
                        minWidth: 200,
                        borderRight: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Tooltip title={rowData?.name || ""} arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 170,
                          }}
                        >
                          {rowData?.name || "-"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {MIDDLE_COLUMNS.map((col) => (
                      <TableCell key={`${rowData.id}-${col.id}`} sx={bodyCellStyle}>
                        {renderCellValue(rowData, col.id)}
                      </TableCell>
                    ))}

                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 2,
                        borderLeft: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit Stop Loss">
                          <IconButton
                            size="small"
                            onClick={() => onEdit(rowData?.id, index)}
                          >
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Logs">
                          <IconButton
                            size="small"
                            onClick={() => onLogs(rowData?.name)}
                          >
                            <HistoryIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default StopLossTable;
